import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { RouterLink } from '@angular/router';
import type { FormlyFieldConfig } from '@ngx-formly/core';

import { ReadonlyDisplayBase } from './readonly-display.base';
import type { Entry } from '../../../../../features/admin/modules/modules.model';
import { UtilityService } from '../../../../services/utility.service';
import { formatDisplayValue } from '../../review-value.util';
import type { ReviewValueContext } from '../../review-value.types';
import { displayContextFor, entryRouteResolverOf } from '../display-context.util';
import type { LinkedEntryEnvelope } from '../readonly.types';
import { READONLY_LINK_STYLES } from './link-styles';
import { ValueDisplayComponent } from './value-display.component';

/** One part of the answer: the name the user picked, and where that item lives (if anywhere). */
interface LinkedEntryPart {
  text: string;
  route: string[] | null;
}

/**
 * Read display for answers that REFERENCE another entry (#21239, task 21244).
 *
 * A linked answer is not a piece of text that happens to name a company — it is a pointer at
 * that company's record. In read mode that is exactly where the reader wants to go on, so the
 * name becomes a link to `/{module slug}/{entry id}` while the row itself stays what it was: the
 * picked name, formatted by the ONE shared text rule (`formatDisplayValue`, which reads
 * `selectedInput`/`name` off the envelope).
 *
 * WHICH field types get this display is decided in `readonly-display.registry.ts`, and only the
 * ones whose stored value really is an envelope. Measured per component (2026-09-09):
 * - `select-linked-entry` stores whole `Entry` envelopes — `{ id, moduleId, entry: { …fields } }`,
 *   NO `selectedInput` (select-linked-entries.component.ts:474-484) → linked, but only nameable
 *   through `props.cardTitle` (see `labelFor` below);
 * - `autocomplete` stores `{ ...entry, selectedInput }` for a module-backed option set with
 *   `moduleValueType: 'entry'` (dynamic-options.service.ts:632-647) → linked;
 * - `dynamic-module-autocomplete` forces `moduleValueType: 'entry-small'`
 *   (dynamic-module-autocomplete.component.ts:150-158), so its value is `{ selectedInput, id }`
 *   with NO module — nothing to build a route from; it stays on the text display;
 * - `module-select` stores a MODULE id (module-select.component.ts:81-86), `linked-value` a
 *   rendered string (linked-value.component.ts:452), and `linkage-select` / `linkage-tags` never
 *   write their own control at all (they create linkage entries through the API) — none of them
 *   is an entry reference.
 *
 * Presentational: the label comes from the value, the route from `formState.resolveEntryRoute`
 * (`entryRouteResolverOf`). Without a resolver — Storybook, a standalone form, a page that never
 * published one — every part falls through to the shared text display, so the row reads exactly
 * as it did before this display existed rather than turning into a dead anchor.
 */
@Component({
  selector: 'dd-linked-entry-display',
  imports: [RouterLink, ValueDisplayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: READONLY_LINK_STYLES,
  // The ', ' separator is a real text node, not a CSS ::after: a read row is copied and read by
  // assistive tech, and generated content is in neither. It matches the join `formatDisplayValue`
  // uses for arrays, so a linked multi-value row reads like every other multi-value row.
  template: `
    @if (parts(); as parts) {
      @for (part of parts; track $index) {
        @if (part.route; as route) {
          <a [routerLink]="route">{{ part.text }}</a>
        } @else {
          <span>{{ part.text }}</span>
        }
        @if (!$last) {
          <span>, </span>
        }
      }
    } @else {
      <dd-value-display [field]="field()" [value]="value()" />
    }
  `,
})
export class LinkedEntryDisplayComponent extends ReadonlyDisplayBase {
  /**
   * The answer split into linkable parts, or `null` for "let the text display handle it".
   *
   * `null` — not an empty array — only when NOTHING is nameable: an empty value (which has to
   * read as the row's em dash) or a plain string that formats to nothing. The moment at least one
   * part has text, `parts` renders it — as an anchor where a route resolved, as plain text where
   * it did not (no route resolver published, or the resolver cannot place the module). A part
   * with a name but no route must still print that name: falling through to `dd-value-display`
   * here would lose it, because that display's shared formatter does not know about this field's
   * `cardTitle` (see `labelFor` / `cardTitleOf` below) and would print the em dash for the exact
   * envelope this display exists to name (#C7).
   */
  readonly parts = computed<LinkedEntryPart[] | null>(() => {
    const raw = this.value();
    const items = Array.isArray(raw) ? raw : [raw];
    const field = this.field();
    const resolve = entryRouteResolverOf(field);
    const context = displayContextFor(field);

    const parts = items
      .map(item => {
        const route = resolve && isEnvelope(item) ? resolve(item) : null;
        return {
          // `labelFor` runs `formatDisplayValue` per ITEM, not once over the whole array — for a
          // multi-value answer with a configured addonLeft/addonRight, each part repeats the
          // unit instead of the row carrying it once (formatDisplayValue's own array path avoids
          // exactly this by calling `withAddons` after the join). Pre-existing for the linked
          // case; the em-dash fix above (#C7) now also routes text-only multi-value answers
          // through this same per-item path.
          text: labelFor(item, field, context),
          // An empty array counts as "nowhere": `[]` is truthy, and a routerLink built from it
          // would render an anchor to the current page.
          route: route?.length ? route : null,
        };
      })
      // An item the shared formatter cannot name has nothing to show — an anchor with no text
      // is an invisible link, which is worse than the row's em dash.
      .filter(part => part.text !== '');

    return parts.length ? parts : null;
  });
}

/** An object answer — the only shape a route can be built from; scalars are typed-in text. */
function isEnvelope(item: unknown): item is LinkedEntryEnvelope {
  return !!item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * The name to print for ONE answer: the field's own card title first, the shared text rule after.
 *
 * The card title is not a nicety — for `select-linked-entry` it is the only name there is.
 * Measured 2026-09-09 on `{ id, moduleId, entry: { bedrijfsnaam: 'Transport BV' } }` (the shape
 * that field really stores): the shared formatter has no candidate key for it and returns `null`,
 * so the row printed the em dash — the very type this display was registered for first read as
 * "no answer". Its editor names those cards with `UtilityService.keyPathToValue(entry, cardTitle)`
 * (select-linked-entries.component.ts:333), so read mode names them the same way and the two
 * modes cannot disagree about what the user picked.
 *
 * Order matters and is deliberate: `cardTitle` is only ever configured for `select-linked-entry`
 * (form-builder.component.ts:2286-2289 hides it for every other type), so it can never override
 * the `selectedInput` of an autocomplete answer. The picked value still goes through the shared
 * formatter, so a title that happens to be a date or a boolean reads like every other row —
 * WITHOUT the field's context: the option labels in that context belong to THIS field, not to
 * the linked module's entry, and reusing them here could rename a title that merely shares a
 * value with one of this field's options.
 */
function labelFor(item: unknown, field: FormlyFieldConfig, context: ReviewValueContext): string {
  return cardTitleOf(item, field) ?? formatDisplayValue(item, context) ?? '';
}

/** The configured card title of one envelope, or `null` when the field or the entry has none. */
function cardTitleOf(item: unknown, field: FormlyFieldConfig): string | null {
  const keyPath = field.props?.['cardTitle'];
  if (typeof keyPath !== 'string' || keyPath === '' || !isEnvelope(item)) {
    return null;
  }
  // `keyPathToValue` walks the envelope exactly as the editor does (bracket paths included) and
  // answers `undefined` for a path this entry does not carry — which falls through to the shared
  // rule below, so a module whose entries only sometimes fill the title field still reads.
  const picked = UtilityService.keyPathToValue(item as unknown as Entry, keyPath);
  return formatDisplayValue(picked);
}
